import { createFileRoute, Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { buildHead } from "@/lib/seo";
import { Logo } from "@/components/site/Logo";
import { LeadCaptureForm } from "@/components/site/LeadCaptureForm";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/lp/shopify-agency";
const SOURCE = "lp-shopify-agency";

const FAQS = [
  { q: "How fast can we kick off?", a: "Within 5 business days of the deposit. Most diagnostics are scoped within 48 hours of your form submission." },
  { q: "What does the diagnostic include?", a: "A 2-week paid engagement: performance audit, conversion review, Plus capability gap, and a prioritized 90-day roadmap. The deliverable is yours to keep regardless of next steps." },
  { q: "Do you only work with Shopify Plus?", a: "We work across Shopify and Shopify Plus. Most engagements involve Plus features (Functions, B2B, Markets), but standard Shopify projects are welcome." },
  { q: "What does pricing look like?", a: "Diagnostics start at $2,500. Builds typically run $15K–$75K. Shopify Plus engineering retainers start at $9,000/month for brands requiring ongoing senior support across Functions, B2B, Markets, integrations, complex releases and technical architecture. We confirm scope and price before any commitment." },
  { q: "Who will we work with?", a: "A senior engineer + project lead, both Shopify-certified. No offshore handoffs, no junior account managers." },
];

export const Route = createFileRoute("/lp/shopify-agency")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "en",
      title: "Shopify Agency for serious DTC brands: Shugert",
      description:
        "Talk to a Shopify Select Partner. Diagnostic, scope and kickoff within 5 days. Performance, conversion and Plus engineering for established DTC brands.",
      path: PATH,
      altPath: "/es/lp/agencia-shopify",
      noindex: true,
    }),
  component: LpShopifyAgencyPage,
});

function LpShopifyAgencyPage() {
  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      {/* Minimal header: logo only, no nav */}
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" className="inline-flex items-center" aria-label="Shugert, home">
            <Logo className="h-10 w-auto" />
          </Link>
          <a
            href="#lead-form-repeat"
            className="hidden text-sm font-medium text-foreground hover:opacity-80 sm:inline"
          >
            Request diagnostic →
          </a>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero + form */}
        <section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Shopify Select Partner · Since 2015
              </p>
              <h1 className="mt-4 text-balance text-4xl font-semibold leading-[1.05] text-foreground sm:text-5xl">
                The Shopify partner serious DTC brands actually keep.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Performance, conversion, and Plus engineering, built by senior engineers, scoped before you commit, kicked off within 5 business days.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Diagnostic delivered in 2 weeks, yours to keep.",
                  "Fixed-price scope. No surprise invoices.",
                  "Senior engineers only. No offshore handoffs.",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-sm text-foreground">
                    <Check className="mt-0.5 h-5 w-5 flex-none text-emerald-500" aria-hidden="true" />
                    {x}
                  </li>
                ))}
              </ul>
              <div className="mt-10 grid grid-cols-2 gap-4 text-sm text-muted-foreground sm:grid-cols-4">
                <div><strong className="text-foreground block">750+</strong> engagements</div>
                <div><strong className="text-foreground block">35</strong> markets</div>
                <div><strong className="text-foreground block">2012</strong> founded</div>
                <div><strong className="text-foreground block">Select</strong> Partner</div>
              </div>
            </div>

            <div id="lead-form-hero">
              <LeadCaptureForm locale="en" source={SOURCE} variant="hero" />
            </div>
          </div>
        </section>

        {/* Value props */}
        <section className="border-t border-border bg-muted/20">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">What you get</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                { t: "Diagnostic in 2 weeks", d: "Performance, conversion and Plus capability audit. Prioritized 90-day roadmap with effort + revenue impact estimates." },
                { t: "Scoped plan, fixed price", d: "We share scope and price before you commit. No retainer required. Most projects run $15K–$75K with a clear before/after." },
                { t: "Kickoff in 5 days", d: "From form to first engineering call within one business week. Most diagnostics close within 90 days." },
              ].map((x) => (
                <div key={x.t} className="rounded-2xl border border-border bg-card p-6">
                  <h3 className="font-semibold text-foreground">{x.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proof */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">Brands we've shipped for</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                { metric: "+38%", label: "mobile conversion lift", note: "DTC apparel, 6 weeks" },
                { metric: "1.1s", label: "LCP after rebuild", note: "Plus beauty brand, prior 3.8s" },
                { metric: "$2.1M", label: "incremental revenue / yr", note: "Functions + checkout extensibility" },
              ].map((x) => (
                <div key={x.label} className="rounded-2xl border border-border bg-card p-6">
                  <p className="text-3xl font-semibold text-foreground sm:text-4xl">{x.metric}</p>
                  <p className="mt-2 text-sm font-medium text-foreground">{x.label}</p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{x.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">Common questions</h2>
            <div className="mt-8 space-y-4">
              {FAQS.map((f) => (
                <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
                  <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-foreground">
                    {f.q}
                    <span className="ml-4 text-muted-foreground transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Repeat form */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-2xl px-6 py-16">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">
                Ready to scope your project?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Fill the form, we reply within 48 hours, often the same day.
              </p>
            </div>
            <div className="mt-10" id="lead-form-repeat">
              <LeadCaptureForm locale="en" source={SOURCE} variant="repeat" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-xs text-muted-foreground sm:flex-row">
          <p>© {new Date().getFullYear()} Shugert Agency. All rights reserved.</p>
          <nav className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <a href="mailto:hello@shugert.com.mx" className="hover:text-foreground">hello@shugert.com.mx</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}